import classnames from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation, withFedopsLogger } from '@wix/yoshi-flow-editor';
import LogOutLink from '../../../account/logout-link';
import Button from '../../../button/button';
import ItemInfo from '../../../item-info/item-info';
import { ItemInfoCover } from './item-info-cover/item-info-cover';
import styles from './item-info-page.scss';
import { PaymentType } from '@wix/wix-vod-constants/api-types';
import {
  getSalePriceInfo,
  isPricingPlanEnabled,
} from '@wix/wix-vod-shared/common';
import { LinkButton } from '../../../buttons/buttons';
import { getCurrentSiteUser } from '../../../../selectors/current-site-user';
import { requestLogin, requestSignUp } from '../../../../utils/auth';
import { sendBiEvent } from '../../../../bi/send-bi-event';
import { goToPricingPlanPicker } from '../../../../worker/actions/go-to-pricing-plan-picker';
import { FedopsInteractionsNames } from '../../../../constants/fedops-interaction-names';

const mapStateToProps = (state, props) => ({
  currentSiteUser: getCurrentSiteUser(state),
  isPricingPlan: isPricingPlanEnabled(props.itemData),
});

const mapDispatchToProps = {
  requestLogin,
  requestSignUp,
  sendBiEvent,
  goToPricingPlanPicker,
};

const ItemInfoPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  class ItemInfoPage extends React.Component {
    static propTypes = {
      itemData: PropTypes.object,
      paymentType: PropTypes.string,
      user: PropTypes.object.isRequired,
      onUserLogin: PropTypes.func.isRequired,
      onLogOut: PropTypes.func,
      onSelectPaymentType: PropTypes.func,
      handleNextStep: PropTypes.func,
      goToPricingPlanPicker: PropTypes.func,
    };

    componentDidMount() {
      const { paymentType, onSelectPaymentType, itemData, fedops } = this.props;

      if (
        paymentType === PaymentType.PURCHASE &&
        !getSalePriceInfo(itemData.dealInfo)
      ) {
        onSelectPaymentType && onSelectPaymentType(PaymentType.RENT);
      }

      fedops.interactionEnded(FedopsInteractionsNames.CASHIER_MODAL_LOAD);
    }

    handleContinueClick = () => {
      const { isPricingPlan, itemData } = this.props;

      if (isPricingPlan) {
        this.props.goToPricingPlanPicker(itemData.id);
      } else {
        this.props.fedops.interactionStarted(
          FedopsInteractionsNames.CASHIER_PAYMENT_PAGE_LOAD,
        );
        this.props.handleNextStep();
      }
    };

    renderContinueButton = () => {
      const { isPricingPlan } = this.props;

      return (
        <Button
          disableTheme
          className={classnames(styles['continue-button'], styles.responsive)}
          dataHook="continue-button"
          onClick={this.handleContinueClick}
        >
          {isPricingPlan
            ? this.props.t('widget.mobile.overlay-button.subscribe.short')
            : this.props.t('widget.payments.continue-button')}
        </Button>
      );
    };

    renderLoggedInPanel() {
      const { user, onLogOut } = this.props;

      return (
        <div className={styles.responsiveWrapper}>
          {this.renderContinueButton()}
          <section className={styles.responsiveUserWrapper}>
            <div className={styles.responsiveUser}>
              <span>{this.props.t('widget.account.signed-as')}</span>
              &nbsp;
              <span className={styles.email}>{user.email}</span>
            </div>
            <LogOutLink
              onLogout={onLogOut}
              prefix={this.props.t('widget.payments-mobile.not-your-account')}
            />
          </section>
        </div>
      );
    }

    handleSignUp = () => {
      this.props.sendBiEvent('widget.signup.clicked');
      this.props.requestSignUp().then(this.props.onUserLogin);
    };

    handleLogin = () => {
      this.props.sendBiEvent('widget.signIn.clicked');
      this.props.requestLogin().then(this.props.onUserLogin);
    };

    renderHeader(paymentType) {
      if (paymentType === PaymentType.MEMBER_ONLY) {
        return (
          <header className={styles.title}>
            <div>{this.props.t('widget.account.sign-in-to-watch.part-1')}</div>
            <div>{this.props.t('widget.account.sign-in-to-watch.part-2')}</div>
          </header>
        );
      }

      return (
        <header className={styles.title}>
          <div>{this.props.t('widget.account.sign-in-to-complete.part-1')}</div>
          <div>{this.props.t('widget.account.sign-in-to-complete.part-2')}</div>
        </header>
      );
    }

    renderSignUpPanel() {
      const { itemData } = this.props;

      return (
        <div className={styles.responsiveWrapper}>
          <Button
            className={classnames(
              `qa-widget-payment-signup-button`,
              styles['sign-up-button'],
              styles.responsive,
            )}
            onClick={this.handleSignUp}
            disableTheme
            dataHook="sign-up-button"
          >
            {itemData.memberOnly
              ? this.props.t('widget.account.sign-up')
              : this.props.t('widget.payments.proceed-checkout')}
          </Button>

          <div className={classnames(styles['sign-in'], styles.responsive)}>
            {`${this.props.t('widget.account.have-account')} `}
            <LinkButton
              onClick={this.handleLogin}
              className={styles['sign-in-link']}
              dataHook="sign-in-button"
            >
              {this.props.t('widget.account.sign-in')}
            </LinkButton>
          </div>
        </div>
      );
    }

    render() {
      const { itemData, paymentType, onPaymentTypeChange, currentSiteUser } =
        this.props;

      const classNames = classnames(styles.wrapper, styles.responsive);

      return (
        <section className={classNames} data-hook="item-info-page">
          <ItemInfoCover
            src={itemData.customCoverUrl || itemData.coverUrl}
            title={itemData.title}
            subtitle={itemData.publisher}
          />

          <ItemInfo
            itemData={itemData}
            onSelectPaymentType={onPaymentTypeChange}
            paymentType={paymentType}
          />

          {currentSiteUser
            ? this.renderLoggedInPanel()
            : this.renderSignUpPanel()}
        </section>
      );
    }
  },
);

export default withFedopsLogger(withTranslation()(ItemInfoPage));
